import _extends from "@babel/runtime/helpers/extends";
/*
 *                     NOTICE
 *            COPYRIGHT 2019 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { defaultRangeExtractor, useVirtualizer } from "@tanstack/react-virtual";
import { SelectionMode } from "abreact/incubator/ui/SharedTypes";
import { CssUtils } from "abreact/incubator/ui/utils/CssUtils";
import { FocusUtils } from "abreact/incubator/ui/utils/FocusUtils";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbCheckbox } from "abreact/input/AbCheckbox";
import { AbItemUtils } from "abreact/misc/AbItemUtils";
import { AbSize, AbSizeContext } from "abreact/misc/AbSize";
import { AbWithTooltip } from "abreact/popup/AbWithTooltip";
import * as AbHooks from "abreact/utils/AbHooks";
import { useCurrentCallback, useOnMount, useOnValuesChange } from "abreact/utils/AbHooks";
import { NOT_AN_ITEM, useSelectionGroupHandlers } from "abreact/utils/AbSelectGroup";
import { TypeToJumpUtils } from "abreact/utils/TypeToJumpUtils";
import { AbEnriched } from "abreact/utils/enrichment/AbEnriched";
import { AB_ITEM_SEPARATOR, AbItemSeparator } from "abweb/collection/AbItemSeparator";
import { AbIterable } from "abweb/collection/AbIterable";
import { AbListHelper } from "abweb/collection/AbListHelper";
import { ElementUtils } from "abweb/utils/ElementUtils";
import { SINGLE_CHARACTER } from "abweb/utils/KeyboardUtils";
import { TypeUtils } from "abweb/utils/TypeUtils";
import * as React from "react";

/**
 * The common properties of an AbList.
 */

/**
 * Utility method to determine if an AbEnrichment allows focus
 */
export const isFocusable = enrichment => {
  return AbEnriched.isSelectable(enrichment) || !!enrichment?.alwaysAllowFocus;
};
export const findFirstFocusable = list => {
  for (let i = 0; i < list.size; i++) {
    const maybeEnrichedItem = list.itemAt(i);
    const enrichment = AbEnriched.getEnrichment(maybeEnrichedItem);
    if (maybeEnrichedItem !== AB_ITEM_SEPARATOR && isFocusable(enrichment)) {
      return [i, maybeEnrichedItem];
    }
  }
  return undefined;
};
export const findLastFocusable = list => {
  const firstFocusable = findFirstFocusable(AbListHelper.from(list).reverse());
  if (!firstFocusable) return undefined;
  return [list.size - firstFocusable[0] - 1, firstFocusable[1]];
};
const SCROLL_BUFFER_PX = 2;
const elementsToScrollTop = (listElement, itemElement) => {
  const middleOffsetTop = itemElement.offsetTop - listElement.offsetHeight / 2 + itemElement.offsetHeight / 2;
  if (itemElement.offsetTop < listElement.scrollTop - itemElement.offsetHeight - SCROLL_BUFFER_PX) {
    // Out of view above, scroll to middle
    return middleOffsetTop;
  }
  if (itemElement.offsetTop < listElement.scrollTop) {
    // Partially out of view above, scroll to top
    return itemElement.offsetTop;
  }
  if (itemElement.offsetTop - listElement.offsetHeight > listElement.scrollTop + SCROLL_BUFFER_PX) {
    // Out of view below, scroll to middle
    return middleOffsetTop;
  }
  if (itemElement.offsetTop + itemElement.offsetHeight - listElement.offsetHeight > listElement.scrollTop) {
    // Partially out of view below, scroll to bottom
    return itemElement.offsetTop + itemElement.offsetHeight - listElement.offsetHeight + 2;
  }
  return listElement.scrollTop; // In view, do nothing
};
const virtualItemToReactKey = item => {
  return `${typeof item.key}-${item.key}`;
};
const DEFAULT_ROW_HEIGHT = 24;
const VIRTUALIZED_OVERSCAN = 3;
const DEFAULT_WIDTH = 200;
const MAX_LIST_HEIGHT = "200vh";
const abRangeExtractor = (range, focusIndex) => {
  const defaultRange = defaultRangeExtractor(range);
  if (focusIndex === undefined || focusIndex >= defaultRange[0] && focusIndex <= defaultRange[defaultRange.length - 1]) {
    return defaultRange;
  }
  return [focusIndex, ...defaultRange].sort((a, b) => a - b);
};
export const AbList = props => {
  // for the purposes of drag and drop - we need to store the dragged item as we cannot access it through the data
  // transfer object in an on drag enter callback

  const isSelectable = enrichment => AbEnriched.isSelectable(enrichment) && !props.disabled;
  const draggable = !props.disabled && (!!props.onReorder || !!props.itemEventHandlers?.onDrag || !!props.itemEventHandlers?.onDragStart || !!props.itemEventHandlers?.onDragEnd);
  const contextSize = React.useContext(AbSizeContext);
  const size = props.size ?? sizeToListSize(contextSize);
  const idFromHook = AbHooks.useUniqueId("ab-list");
  const uniqueId = props.itemIdPrefix ?? idFromHook;
  const uniqueClassname = AbHooks.useUniqueId("ab-list-class");
  const listModel = React.useMemo(() => {
    return AbListHelper.from(props.items);
  }, [props.items]);
  const listModelRef = AbHooks.useCurrentValue(listModel);
  const onFocus = React.useCallback(newIndex => {
    const indexToFocus = newIndex ?? getFocusItem()?.[0];
    if (indexToFocus === undefined) return;
    scrollToIndex(indexToFocus);
    const elem = listScrollElementRef.current?.querySelector(`#${uniqueId}-${indexToFocus}`) ?? null;
    if (elem) {
      FocusUtils.setFocus(elem);
    } else {
      focusRequest.current = {
        index: indexToFocus
      };
    }
  }, [uniqueId]);
  const updateFocusItem = React.useCallback(focus => {
    setFocusItemPair(focus);
    props.onFocusChange?.(AbEnriched.getItem(focus?.[1]));
    onFocus(focus?.[0]);
  }, [onFocus, props]);
  const jumpFocus = index => {
    if (index < 0 || index >= listModel.size) return;
    // focus on the ith index
    const item = listModel.itemAt(index);
    if (item === AB_ITEM_SEPARATOR) return;
    updateFocusItem([index, item]);
  };
  const helpers = useSelectionGroupHandlers({
    selectionModeProps: props,
    keyFunction: props.keyFunction,
    focusIndexFunction: jumpFocus,
    items: listModel.map(item => {
      if (item === AB_ITEM_SEPARATOR) return NOT_AN_ITEM;
      return item;
    }),
    disabled: props.disabled,
    onSelect: props.onSelect,
    onUnselect: props.onUnselect,
    orientation: "vertical"
  });
  const [virtualizedRowHeight, setVirtualizedRowHeight] = React.useState(DEFAULT_ROW_HEIGHT);
  const rowHeightFunction = React.useCallback(index => {
    if (listModelRef.current.itemAt(index) === AB_ITEM_SEPARATOR) return SEPARATOR_HEIGHT;
    return AbEnriched.getEnrichment(listModelRef.current.itemAt(index))?.rowHeight ?? virtualizedRowHeight ?? DEFAULT_ROW_HEIGHT;
  }, [virtualizedRowHeight]);
  const listScrollElementRef = AbHooks.useResizeObserver(() => {
    if (props.scrollToIndex !== undefined && !userInteractedRef.current) {
      callOnScrollIndexChange();
    }
  });
  const getListScrollElement = useCurrentCallback(() => listScrollElementRef.current);
  const keyFunction = AbHooks.useCurrentValue(props.keyFunction);
  const itemToKey = AbHooks.useCurrentCallback((maybeEnrichedItem, index) => {
    if (maybeEnrichedItem === AB_ITEM_SEPARATOR) return index;
    const item = AbEnriched.getItem(maybeEnrichedItem);
    return TypeUtils.itemToKey(item, keyFunction.current);
  });
  const indexToKey = AbHooks.useCurrentCallback(index => {
    const maybeEnrichedItem = listModelRef.current.itemAt(index);
    return itemToKey(maybeEnrichedItem, index);
  });
  const rangeExtractor = useCurrentCallback(range => abRangeExtractor(range, focusItemPair?.[0]));
  const virtualizer = useVirtualizer({
    enabled: !!props.virtualized,
    count: listModel.size,
    getScrollElement: getListScrollElement,
    getItemKey: indexToKey,
    estimateSize: rowHeightFunction,
    overscan: VIRTUALIZED_OVERSCAN,
    rangeExtractor: rangeExtractor,
    useAnimationFrameWithResizeObserver: true
  });
  const measureElement = useCurrentCallback(element => {
    if (element?.clientHeight === 0) return;
    virtualizer.measureElement(element);
  });
  const isItemSelected = item => {
    if (props.selectionMode === SelectionMode.MULTIPLE) {
      const key = TypeUtils.itemToKey(AbEnriched.getItem(item), props.keyFunction);
      return AbIterable.any(props.value, v => TypeUtils.itemToKey(AbEnriched.getItem(v), props.keyFunction) === key);
    } else if (props.selectionMode === SelectionMode.SINGLE) {
      return TypeUtils.equals(AbEnriched.getItem(props.value), AbEnriched.getItem(item), props.keyFunction);
    }
    return false;
  };
  const computeInitialFocusIndexItemHelper = valueArray => {
    if (!valueArray || valueArray.length === 0) return findFirstFocusable(listModel);
    const indexOf = listModel.indexOf;
    const indices = indexOf ? valueArray.map(indexOf).sort() : AbIterable.range(listModel.size);
    for (const i of indices) {
      if (i === -1) continue;
      const modelItem = listModel.itemAt(i);
      if (modelItem !== AB_ITEM_SEPARATOR && isItemSelected(modelItem)) {
        return [i, modelItem];
      }
    }
    return findFirstFocusable(listModel);
  };
  const computeInitialFocusIndexItem = useCurrentCallback(() => {
    switch (props.selectionMode) {
      case SelectionMode.NONE:
      case undefined:
        return findFirstFocusable(listModel);
      case SelectionMode.SINGLE:
        return computeInitialFocusIndexItemHelper(props.value !== undefined ? [props.value] : undefined);
      case SelectionMode.MULTIPLE:
        return computeInitialFocusIndexItemHelper([...props.value]);
    }
  });
  const [focusItemPair, setFocusItemPair] = React.useState(computeInitialFocusIndexItem);
  const userInteractedRef = React.useRef(false);
  const autoFocusFired = React.useRef(false);
  const focusRequest = React.useRef(undefined);
  const currentlyDraggedItem = React.useRef(undefined);
  const [dragLineBeforeIndex, setDragLineBeforeIndex] = React.useState();

  // if virtualized list, by default, don't support type-to-focus
  // if non-virtualized, by default, do support type-to-focus
  // providing an itemRenderer prevents type-to-focus from using defaultItemDisplayStringFunction to match items
  // todo when an item renderer is provided, disable default type-to-focus string matching
  //  (while preserving it for multiselect rich dropdowns, which render checkboxes)
  const defaultItemToString = TypeToJumpUtils.defaultItemToString;
  // we want to follow the rules of hooks, so pass along an empty items array in the
  // cases where we don't want to type to jump
  const itemsForTypeToJump = props.__typeToFocusProps?.enabled === false || props.virtualized && props.__typeToFocusProps?.enabled !== true ? [] : props.items;
  const jumpFocusOnKeypress = AbHooks.useDefaultTypeToJump(itemsForTypeToJump, () => getFocusItem()?.[0] ?? -1, jumpFocus, props.__typeToFocusProps?.itemToString ?? defaultItemToString, props.dataFreshness);
  useOnMount(() => {
    props.__typeToFocusProps?.initialFocusString && jumpFocusOnKeypress(props.__typeToFocusProps.initialFocusString);
  });
  useOnValuesChange(() => {
    const focusItem = getFocusItem();
    if (focusItem === undefined) {
      setFocusItemPair(computeInitialFocusIndexItem());
    }
  }, [props.items, props.dataFreshness]);
  const callOnScrollIndexChange = AbHooks.useCurrentCallback(() => {
    const focusItem = getFocusItem();
    const index = props.scrollToIndex ?? focusItem?.[0];
    scrollToIndex(index);
  });
  React.useEffect(() => {
    callOnScrollIndexChange();
  }, [props.scrollToIndex]);
  React.useEffect(() => {
    if (userInteractedRef.current) return;
    callOnScrollIndexChange();
  }, [virtualizedRowHeight]);
  const getFocusItem = useCurrentCallback(() => {
    if (focusItemPair === undefined) return undefined;
    if (focusItemPair[0] >= listModel.size) {
      setFocusItemPair(undefined);
      return undefined;
    }
    const maybeEnrichedFocusItem = focusItemPair[1];
    const {
      item,
      enrichment
    } = AbEnriched.extract(listModel.itemAt(focusItemPair[0]));
    if (item === AB_ITEM_SEPARATOR || !isFocusable(enrichment) || !TypeUtils.equals(AbEnriched.getItem(maybeEnrichedFocusItem), item, props.keyFunction)) {
      setFocusItemPair(undefined);
      return undefined;
    }
    return focusItemPair;
  });
  const scrollToIndex = useCurrentCallback(index => {
    if (currentlyDraggedItem.current || index === undefined) return;
    const elem = listScrollElementRef.current?.querySelector(`#${uniqueId}-${index}`) ?? null;
    if (props.virtualized) {
      if (!elem || !listScrollElementRef.current) virtualizer.scrollToIndex(index, {
        align: "center"
      }); // Let virtualizer handle cases where element is not mounted
      else virtualizer.scrollToOffset(elementsToScrollTop(listScrollElementRef.current, elem));
    } else {
      if (!elem || !listScrollElementRef.current) return;else listScrollElementRef.current.scroll({
        top: elementsToScrollTop(listScrollElementRef.current, elem)
      });
    }
  });
  const onItemClick = (maybeEnrichedItem, index, e) => {
    helpers.onClick(e, index);
    props.itemEventHandlers?.onClick?.(AbEnriched.getItem(maybeEnrichedItem), e);
  };
  const listBoxItemRenderer = (enrichedItem, index, key) => {
    if (enrichedItem === AB_ITEM_SEPARATOR) {
      return /*#__PURE__*/React.createElement("li", {
        className: "ab-item-separator",
        key: key,
        ref: measureElement,
        "data-index": index
      }, /*#__PURE__*/React.createElement("hr", null));
    } else {
      const {
        item,
        enrichment
      } = AbEnriched.extract(enrichedItem);
      const itemDraggable = draggable && !enrichment?.disableDrag;
      const disabled = props.disabled || enrichment?.disabled;
      const displayOnly = enrichment?.displayOnly;
      const rowHeight = enrichment?.rowHeight ?? undefined;
      const theFocusItem = getFocusItem();
      const isFocusItem = isFocusable(enrichment) && theFocusItem && theFocusItem[0] === index;
      const isSelected = isItemSelected(enrichedItem);
      const renderTargetDropLineBefore = index === dragLineBeforeIndex;
      const renderTargetDropLineAfter = index === listModel.size - 1 && index + 1 === dragLineBeforeIndex;
      const classes = ReactUtils.classNamesString(["ab-list-item", isSelected && "ab-selected", disabled && "ab-disabled", displayOnly && "ab-displayonly", enrichment?.alwaysAllowFocus && "ab-always-allow-focus", (renderTargetDropLineBefore || renderTargetDropLineAfter) && "drag-and-drop-hover-target", renderTargetDropLineBefore && "top-half-drag", renderTargetDropLineAfter && "bottom-half-drag", enrichment?.className]);
      const ariaSelectionObj = props.selectionMode === undefined || props.selectionMode === SelectionMode.NONE ? {} : {
        [props.selectionMode === SelectionMode.SINGLE ? "aria-selected" : "aria-checked"]: isSelected ? true : isSelectable(enrichment) ? false : undefined
      };
      const styleWithRowHeight = {
        "--ab-list-row-height": rowHeight
      };
      const tabIndex = isFocusItem ? props.tabIndex ?? 0 : -1;
      return /*#__PURE__*/React.createElement(AbWithTooltip, {
        tooltip: enrichment?.tooltip,
        key: key
      }, /*#__PURE__*/React.createElement("li", _extends({
        className: classes,
        "aria-expanded": enrichment?.ariaExpanded,
        "aria-setsize": props.virtualized ? listModel.size : undefined,
        "aria-posinset": props.virtualized ? index + 1 : undefined // 1 indexed
        ,
        "aria-level": props.virtualized ? enrichment?.ariaLevel : undefined
      }, ariaSelectionObj, {
        role: props.__htmlProps?.role === "tree" ? "treeitem" : undefined,
        id: `${uniqueId}-${index}` // AbTree depends on this id for aria-owns logic
        ,
        onClick: disabled || displayOnly ? undefined : e => onItemClick(enrichedItem, index, e),
        onKeyDown: disabled || displayOnly || !props.itemEventHandlers?.onKeyDown ? undefined : e => props.itemEventHandlers?.onKeyDown?.(item, e),
        onDoubleClick: disabled || displayOnly || !props.itemEventHandlers?.onDoubleClick ? undefined : e => props.itemEventHandlers?.onDoubleClick?.(item, e),
        onDrag: disabled || displayOnly || !props.itemEventHandlers?.onDrag ? undefined : e => props.itemEventHandlers?.onDrag?.(item, e),
        tabIndex: !isFocusable(enrichment) ? undefined : tabIndex,
        style: styleWithRowHeight,
        draggable: itemDraggable,
        "data-index": index,
        ref: ReactUtils.mergeRefs(measureElement, element => {
          if (!element) return;
          if (index === focusRequest.current?.index) {
            FocusUtils.setFocus(element);
            focusRequest.current = undefined;
          }
        }),
        onDragStart: itemDraggable ? e => onDragStart(e, enrichedItem, index) : undefined
        // Note this is required to make the element a valid drop target
        ,
        onDragOver: itemDraggable ? e => onDragOver(e, index) : undefined,
        onDrop: itemDraggable ? e => onDrop(e, index) : undefined,
        onDragEnd: e => onDragEnd(e, enrichedItem)
      }), props.selectionMode === SelectionMode.MULTIPLE && !displayOnly && /*#__PURE__*/React.createElement(AbCheckbox, {
        className: "ab-list-item-checkbox",
        tabIndex: -1,
        value: isSelected,
        disabled: disabled,
        onChange: () => {
          // noop
        }
      }), (props.itemRenderer ?? AbItemUtils.defaultItemDisplayName)(item, enrichment)));
    }
  };
  const getReorderEvent = (e, draggedItem, listItemIndex) => {
    if (!props.onReorder || draggedItem === undefined || draggedItem.index === listItemIndex) return undefined;
    const targetRectangle = e.currentTarget.getBoundingClientRect();
    // mouse Y position relative to target dragover element
    const mouseRelativeYPosition = e.clientY - targetRectangle.top;
    const bottomHalfDropEvent = mouseRelativeYPosition > targetRectangle.height / 2;
    const toIndex = listItemIndex + (bottomHalfDropEvent ? 1 : 0) + (listItemIndex > draggedItem.index ? -1 : 0);
    if (draggedItem.index === toIndex) return undefined;
    const reorderEvent = {
      fromIndex: draggedItem.index,
      toIndex: toIndex,
      dragTargetHalf: bottomHalfDropEvent ? "bottom" : "top"
    };
    if (!props.allowDropFunction || props.allowDropFunction(reorderEvent)) return reorderEvent;
    return undefined;
  };
  // on drag over if top half put top line class name, if bottom half put bottom class name
  // when dropping on top half drop above, when dropping on bottom half drop below
  const onDragOver = (e, index) => {
    // must ensure the drag target is from the same list
    e.preventDefault();
    const reorderEvent = getReorderEvent(e, currentlyDraggedItem.current, index);
    if (reorderEvent) {
      e.dataTransfer.dropEffect = "move";
      setDragLineBeforeIndex(reorderEvent.toIndex + (reorderEvent.toIndex > reorderEvent.fromIndex ? 1 : 0));
    } else {
      e.dataTransfer.dropEffect = "none";
      setDragLineBeforeIndex(undefined);
    }
  };
  const onDragStart = (e, item, index) => {
    e.dataTransfer.effectAllowed = "move";
    // Stop propagation so if the list is in a draggable parent, the parent won't want to handle drag events as well.
    props.itemEventHandlers?.onDragStart?.(AbEnriched.getItem(item), e);
    e.stopPropagation();
    currentlyDraggedItem.current = {
      item,
      index
    };
  };
  const onDrop = (e, index) => {
    e.preventDefault();
    const reorderEvent = getReorderEvent(e, currentlyDraggedItem.current, index);
    reorderEvent && props.onReorder?.(reorderEvent);
    setDragLineBeforeIndex(undefined);
    currentlyDraggedItem.current = undefined;
  };
  const onDragEnd = (e, item) => {
    props.itemEventHandlers?.onDragEnd?.(AbEnriched.getItem(item), e);
    currentlyDraggedItem.current = undefined;
  };
  const autoFocusFunc = () => {
    if (autoFocusFired.current || !props.autoFocus) return;
    window.requestAnimationFrame(() => onFocus());
    autoFocusFired.current = true;
  };
  const virtualizedListRefFunction = listElement => {
    if (!listElement) return;
    const newRowMinHeight = ElementUtils.getCssAsNumber("--ab-list-row-min-height", DEFAULT_ROW_HEIGHT, listElement);
    const newRowHeight = ElementUtils.getCssAsNumber("--ab-list-row-height", newRowMinHeight, listElement);
    if (userInteractedRef.current) return;
    setVirtualizedRowHeight(newRowHeight);
    autoFocusFunc();
  };
  React.useEffect(() => {
    virtualizer.measure();
  }, [virtualizedRowHeight, virtualizer]);
  const calcRole = () => {
    if (props.__htmlProps?.role) return props.__htmlProps.role;
    if (props.selectionMode === SelectionMode.SINGLE || props.selectionMode === SelectionMode.MULTIPLE) return "listbox";
    return undefined;
  };
  const focusFirstFocusable = React.useCallback(() => {
    const result = findFirstFocusable(listModel);
    result && updateFocusItem(result);
  }, [listModel, updateFocusItem]);
  const focusLastFocusable = React.useCallback(() => {
    const result = findLastFocusable(listModel);
    result && updateFocusItem(result);
  }, [listModel, updateFocusItem]);
  const isFirstFocused = React.useCallback(() => {
    return getFocusItem()?.[0] === findFirstFocusable(listModel)?.[0];
  }, [listModel]);
  const isLastFocused = React.useCallback(() => {
    return getFocusItem()?.[0] === findLastFocusable(listModel)?.[0];
  }, [listModel]);
  React.useImperativeHandle(props.abRef, () => ({
    setFocusItem: updateFocusItem,
    getFocusItem: () => {
      const maybeEnrichedFocusItem = getFocusItem();
      return maybeEnrichedFocusItem ? [maybeEnrichedFocusItem[0], AbEnriched.getItem(maybeEnrichedFocusItem[1])] : undefined;
    },
    incrementSelection: increment => {
      updateFocusItem(incrementSelection(listModel, getFocusItem()?.[0] ?? (increment === 1 ? -1 : listModel.size), increment));
    },
    scrollToIndex: index => scrollToIndex(index),
    focusFirstFocusable: focusFirstFocusable,
    focusLastFocusable: focusLastFocusable,
    isLastFocused: isLastFocused,
    isFirstFocused: isFirstFocused
  }), [updateFocusItem, focusFirstFocusable, focusLastFocusable, isLastFocused, isFirstFocused, listModel]);
  const {
    disabled,
    tabIndex,
    autoFocus,
    ...componentProps
  } = ReactUtils.inputProps("ab-list", props, [uniqueClassname, size, props.selectionMode === SelectionMode.SINGLE && "ab-list-highlight-selection", props.selectionMode === SelectionMode.MULTIPLE && props.selectionConfig?.selectionMode === "few" && "ab-list-highlight-selection"]);
  return /*#__PURE__*/React.createElement(AbSizeContext.Provider, {
    value: AbSize.EXTRA_SMALL
  }, /*#__PURE__*/React.createElement(AbWithTooltip, {
    tooltip: props.tooltip
  }, /*#__PURE__*/React.createElement("div", _extends({}, componentProps, {
    tabIndex: -1,
    onKeyDown: e => {
      if (SINGLE_CHARACTER.test(e.key)) {
        jumpFocusOnKeypress(e.key);
      } else {
        // enter, del, insert and vertical arrow keys clear out the type-to-jump matching string
        !TypeToJumpUtils.DONT_CLEAR_STRING_KEYS.includes(e.key) && jumpFocusOnKeypress(undefined);
      }
      if (focusItemPair) {
        helpers.onKeyDown(e, focusItemPair[0]);
      }
      props.__htmlProps?.onKeyDown?.(e);
    },
    onContextMenu: props.__htmlProps?.onContextMenu,
    onKeyPress: props.__htmlProps?.onKeyPress,
    onWheel: () => userInteractedRef.current = true,
    onMouseDown: e => {
      userInteractedRef.current = true;
      if (e.shiftKey) e.preventDefault(); // Prevents unwanted highlighting of text etc.
    },
    ref: listScrollElementRef,
    onMouseLeave: draggable ? () => setDragLineBeforeIndex(undefined) : undefined
  }), /*#__PURE__*/React.createElement("ul", {
    className: "ab-list-ul",
    ref: virtualizedListRefFunction,
    role: calcRole(),
    "aria-multiselectable": props.selectionMode === SelectionMode.MULTIPLE,
    id: `${uniqueId}-list`,
    tabIndex: -1,
    style: {
      height: props.virtualized ? virtualizer.getTotalSize() : undefined
    }
  }, props.virtualized && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("style", null, `.${uniqueClassname} {
                  width: ${CssUtils.stringOrNumberToCssString(DEFAULT_WIDTH)};
                  max-height: ${CssUtils.stringOrNumberToCssString(MAX_LIST_HEIGHT)};
                }`), virtualizer.getVirtualItems().flatMap((item, virtualIndex, virtualItems) => {
    const renderPillow = height => /*#__PURE__*/React.createElement("div", {
      style: {
        height
      },
      key: `p-${virtualIndex}}`
    });
    if (!item) return [];
    let pillow = null;
    if (virtualIndex === 0 && item.index > 0) {
      pillow = renderPillow(item.start); // Pillow from start to item
    } else if (virtualIndex > 0 && item.index > virtualItems[virtualIndex - 1].index + 1) {
      pillow = renderPillow(item.start - virtualItems[virtualIndex - 1].end); // Pillow from previous item to this item
    }
    return [pillow, listBoxItemRenderer(listModel.itemAt(item.index), item.index, virtualItemToReactKey(item))];
  })), !props.virtualized && listModel.map((item, index) => listBoxItemRenderer(item, index, itemToKey(item, index)))))));
};
export const SEPARATOR = AB_ITEM_SEPARATOR;
export const SEPARATOR_HEIGHT = 11;
export const incrementSelection = (listModel, currentIndex, increment) => {
  if (listModel.size > 0) {
    const firstIndex = (increment + (currentIndex ?? (increment === 1 ? -1 : listModel.size)) + listModel.size) % listModel.size;
    let index = firstIndex;
    do {
      const maybeEnrichedItem = listModel.itemAt(index);
      const enrichment = AbEnriched.getEnrichment(maybeEnrichedItem);
      if (AbItemSeparator.isNotSeparator(maybeEnrichedItem) && isFocusable(enrichment)) {
        return [index, maybeEnrichedItem];
      }
      index = (index + increment + listModel.size) % listModel.size;
    } while (index !== firstIndex);
  }
  return undefined;
};
export const sizeToListSize = size => {
  switch (size) {
    case AbSize.EXTRA_SMALL:
    case AbSize.SMALL:
      return AbSize.SMALL;
    case AbSize.MEDIUM:
    case AbSize.CUSTOM:
    case undefined:
      return AbSize.MEDIUM;
    case AbSize.LARGE:
    case AbSize.EXTRA_LARGE:
      return AbSize.LARGE;
  }
};
//# sourceMappingURL=AbList.js.map
